import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useDependency, useOptionalDependency } from 'ditox-react';
import { useSubscription } from 'observable-hooks';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { EMPTY } from 'rxjs';
import { COMM_MANAGER_TOKEN, I18N_TOKEN, INTERCOM_MANAGER_TOKEN, SERVICE_WORKER_MANAGER_TOKEN } from '@/container';
import useSignOut from '@/hooks/users/use-sign-out';
import type { CommEvent } from '@/models/events/comm-event';
import type { CommMessage } from '@/models/messages/comm-message';
import ROUTES from '@/routes';
import { promisifySessionsHydration, useConversationSessionStore } from '@/stores/conversation-session';
import { ensureAllSessions } from '@/utils/cache-session';
import { APP_VERSION_HASH } from '@/utils/constants';
import { timeoutPromise } from '@/utils/promise';
import { notificationSettingsQueryOptions } from '../settings/use-notification-settings';

export default function useComm() {
  const commManager = useOptionalDependency(COMM_MANAGER_TOKEN);
  const intercomManager = useOptionalDependency(INTERCOM_MANAGER_TOKEN);
  const serviceWorkerManager = useOptionalDependency(SERVICE_WORKER_MANAGER_TOKEN);
  const i18n = useOptionalDependency(I18N_TOKEN);
  const navigate = useNavigate();
  const { mutate: signOut } = useSignOut();
  const queryClient = useQueryClient();
  const { subscribeSessionByUserId, unSubscribeSessionByUserId } = useConversationSessionStore();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  useSubscription(
    commManager?.event$ ?? EMPTY,
    async (event) => {
      switch (event.event) {
        case 'sign-out':
          signOut();
          break;
        case 'change-locale':
          i18n?.changeLanguage(event.data.locale.toLowerCase());
          dayjs.locale(event.data.locale.toLowerCase());
          break;
        case 'close':
          navigate(ROUTES.MESSAGES.$path(), { state: { from: 'comm' } });
          break;
        case 'navigate-back':
          if (location.state?.from === 'comm' || searchParams.get('from') === 'onboard') {
            commManager?.dispatch({ event: 'dismiss' });
          } else {
            navigate(-1);
          }
          break;
        case 'change-notification-settings':
          await queryClient.invalidateQueries(notificationSettingsQueryOptions);
          break;
        case 'check-version-update': {
          const response = await serviceWorkerManager?.checkVersionUpdate();
          response &&
            commManager?.dispatch({
              event: 'version-update',
              data: {
                version: response?.key,
                hasUpdate: response?.hasUpdate,
              },
            });
          break;
        }
        case 'trigger-version-update':
          await serviceWorkerManager?.triggerServiceWorkerUpdate();
          commManager?.dispatch({ event: 'reload', data: { cause: 'sw-update-comm' } });
          break;
        case 'session':
          switch (event.data.type) {
            case 'group':
              navigate(ROUTES.MESSAGES.GROUPS.$buildPath({ params: { id: event.data.id } }), {
                state: { from: 'comm' },
              });
              break;
            case 'session':
              navigate(ROUTES.MESSAGES.SESSIONS.$buildPath({ params: { id: event.data.id } }), {
                state: { from: 'comm' },
              });
              break;
          }
          break;
        case 'request-user-session':
          navigate(ROUTES.USER_BY_ID.$buildPath({ params: { id: event.data.id } }), {
            state: { from: 'comm', layout: 'mini' },
          });
          break;
        case 'group-info':
          navigate(ROUTES.GROUP_BY_ID.$buildPath({ params: { id: event.data.id } }), { state: { from: 'comm' } });
          break;
        case 'subscribe-session':
          switch (event.data.type) {
            case 'new-message':
              if (event.data.session?.type === 'user') {
                subscribeSessionByUserId(event.data.session.id);
              }
          }
          break;
        case 'unsubscribe-session':
          switch (event.data.type) {
            case 'new-message':
              if (event.data.session?.type === 'user') {
                unSubscribeSessionByUserId(event.data.session.id);
              }
          }
          break;
      }
    },
    undefined,
    () => {
      commManager?.dispatch({ event: 'terminated' });
    },
  );

  useSubscription(intercomManager?.event$ ?? EMPTY, async (event) => {
    switch (event.event) {
      case 'clear-all-device-session':
        await promisifySessionsHydration();
        await ensureAllSessions();
        useConversationSessionStore.getState().hideAllCurrentSessions();

        break;
    }
  });

  useEffect(() => {
    commManager?.dispatch({
      event: 'initialized',
      data: { version: APP_VERSION_HASH },
    });
  }, [commManager?.dispatch]);

  return {
    commManager,
    intercomManager,
  };
}

export function useCommDispatcher() {
  const manager = useOptionalDependency(COMM_MANAGER_TOKEN);

  const handleDispatch = useCallback(
    (message: CommMessage) => {
      manager?.dispatch(message);
    },
    [manager],
  );

  const handleAwaitDispatch = useCallback(
    (message: CommMessage, awaitEventNames: Array<CommEvent['event']>) =>
      timeoutPromise(
        () =>
          new Promise<CommEvent>((resolve, reject) => {
            if (!manager) return reject('COMM_MANAGER_NOT_FOUND');

            const subscription = manager.event$.subscribe({
              next: (event) => {
                if (awaitEventNames.includes(event.event)) {
                  subscription.unsubscribe();
                  resolve(event);
                }
              },
              error: reject,
            });

            manager?.dispatch(message);
          }),
        15_000,
      ),
    [manager],
  );

  return {
    dispatch: handleDispatch,
    awaitDispatch: handleAwaitDispatch,
  };
}
